import Papa from 'papaparse';
import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { inCommands } from '../../../concepts/commands/CommandRegistry';
import { Component } from '../../../concepts/objects';
import { Store } from '../../../concepts/revive/Store';
import { Fauxdown } from '../../../concepts/text/Fauxdown';
import { ClipboardService, ClipDataBuilder } from '../../../platform/Clipboard';
import { IsActuallyConcealed } from '../../packages/conceal/Concealable';
import { Graphic } from '../../packages/graphics/Graphic';
import { EditorSession } from '../EditorSession';
import { Physical, RelativeTo } from '../entities/Physical';
import { Physicals } from '../entities/Physicals';
import { ContentInjector } from '../injection/ContentInjector';
import { DeqoData, DeqoStyleData } from '../injection/DeqoData';
import { Selection } from '../Selection';

const clipboardStore = Store.create({
    pasteCount: 0,
});

type Cell = { id: string, depth: number, value: string };
type Row = Cell[];

const readRow = (row: Row) => {
    row.sort((a, b) => a.depth - b.depth);
    const headings = row.map(x => x.id);
    const key = headings.join('/');
    const values = row.map(x => x.value);
    return [key, headings, values] as const;
};

const formatRows = (rows: Row[]): string => {

    // Do all rows contain a single cell with the same id?
    {
        const firstId = rows[0][0].id;
        const yup = !rows.find(x => x.length > 1 || x[0].id !== firstId);
        if (yup) {
            // If so, just return the data from the single cell in every row (no title).
            return rows.map(x => x[0].value).join('\r\n');
        }
    }

    const output = [] as string[][];
    let headingKey = null as string;

    for (const r of rows) {
        const [hk, hd, tr] = readRow(r);
        if (headingKey !== hk) {
            headingKey = hk;
            output.push(hd);
        }
        output.push(tr);
    }

    return Papa.unparse(output);
};

const extractRecursive = (scope: Row, entity: Component): Row[] => {

    const desc = Component.describe(entity);
    const curr = [] as Row;

    for (const f of entity.fields) {
        if (f.type !== 'text') continue;
        if (curr.length === 0) {
            curr.push(...scope);
        }
        curr.push({
            id: `${f.name}`,
            depth: entity.depth(),
            // TODO: For Lenses
            // This is abstraction leak hell and needs improving when we do Lenses
            value: IsActuallyConcealed.get(entity) ? 'Private Text' : f.toString(),
        });
    }

    if (entity.fields.length === 0) {
        for (const f of entity.as(Physical)?.uiFields ?? []) {
            const prop = (desc.properties.find(x => x.name === f.name));
            if (prop?.type?.name === 'string') {
                const value = entity.get(f.name);
                curr.push({
                    id: `${f.name}`,
                    depth: entity.depth(),
                    // TODO: For Lenses
                    // This is abstraction leak hell and needs improving when we do Lenses
                    value: IsActuallyConcealed.get(entity) ? 'Private Text' : Fauxdown.toExportableForm(value),
                });
            }
        }
    }

    const image = entity.as(Graphic)?.url;
    if (image) {
        curr.push({
            id: `${entity.type}.image`,
            depth: entity.depth(),
            value: `<img src="${image}">`,
        });
    }

    const next = entity.children.flatMap(x => extractRecursive(curr, x));
    if (next.length) {
        return next;
    }
    else if (curr.length) {
        return [curr];
    }
    else {
        return [];
    }
};

const extractFieldData = (subjects: Physical[]) => {
    const rows = subjects.flatMap(x => extractRecursive([], x));
    return !!rows.length ? formatRows(rows) : null;
};

export const copy = defineCommand('copy', 'Copy', (r) => {

    // Copying is weird because in the browser we can't access the native clipboard API, we have to hack some stuff.  Specifically, we want
    // it to be the case that when users copy and paste objects with text content from the board into another application that they see the
    // "content" of their selection pasted.  However, we also want it to be the case that when they paste content from one board to another
    // (or to the same board) that they see the element data of their selection pasted.
    //
    // To achieve this, we inspect the selection and if _any_ of the selected elements implement the HasFields convention, we use the Fields
    // API to extract the content data from those elements.  If there are multiple fields, we format this as CSV with headings, otherwise we
    // just output the content as text with a line per element.  If no HasField elements are selected, we revert to the tuple output as the
    // text representation.

    const clipboard = r.call(ClipboardService);
    const selection = r.call(EditorSession).get(Selection);

    if (!selection.size) return;

    // When copying, nested elements will be taken care of by the tuplization process so we only need to deal with the top elements.  We
    // also want to compute the tuples for these top elements relative to the model, rather than the default of the parent, so that the data
    // can be pasted back into the true root maintaining the position.  We use the true root rather than selected root since copy and paste
    // may take place through different root contexts.
    const elmts = Physicals.topToBottom(Physicals.top(selection, true));
    const elmtData = DeqoData.wrap(elmts.map(x => x.toTuple(RelativeTo.Model)));
    const textData = extractFieldData(Array.from(selection)) || JSON.stringify(elmtData, null, 4);

    const data = new ClipDataBuilder();
    data.setText(textData);
    data.setHtmlWithEmbeddedData(textData, elmtData);

    // We need to do this for fallback clipboard:
    data.setDeqoData(elmtData);

    clipboard.write(data.build());

    // Reset paste count to prevent paste stacking further
    clipboardStore.update({ pasteCount: 0 });
});

export const copyData = defineCommand('copyData', 'Copy Data', (r) => {

    const clipboard = r.call(ClipboardService);
    const selection = r.call(EditorSession).get(Selection);

    if (!selection.size) return;

    // When copying, nested elements will be taken care of by the tuplization process so we only need to deal with the top elements.  We
    // also want to compute the tuples for these top elements relative to the model, rather than the default of the parent, so that the data
    // can be pasted back into the true root maintaining the position.  We use the true root rather than selected root since copy and paste
    // may take place through different root contexts.
    const elmts = Physicals.topToBottom(Physicals.top(selection, true));
    const elmtData = DeqoData.wrap(elmts.map(x => x.toTuple(RelativeTo.Model)));
    const textData = JSON.stringify(elmtData, null, 4);

    const data = new ClipDataBuilder();
    data.setText(textData);
    data.setHtmlWithEmbeddedData(textData, elmtData);

    // We need to do this for fallback clipboard:
    data.setDeqoData(elmtData);

    clipboard.write(data.build());

    // Reset paste count to prevent paste stacking further
    clipboardStore.update({ pasteCount: 0 });
});

export const copyJson = defineCommand('copyJson', 'Copy Json', (r) => {

    const clipboard = r.call(ClipboardService);
    const selection = r.call(EditorSession).get(Selection);

    if (!selection.size) return;

    // When copying, nested elements will be taken care of by the tuplization process so we only need to deal with the top elements.  We
    // also want to compute the tuples for these top elements relative to the model, rather than the default of the parent, so that the data
    // can be pasted back into the true root maintaining the position.  We use the true root rather than selected root since copy and paste
    // may take place through different root contexts.
    const elmts = Physicals.topToBottom(Physicals.top(selection, true));
    const elmtData = elmts.map(x => x.toJson(RelativeTo.Model));
    const textData = JSON.stringify(elmtData, null, 4);

    const data = new ClipDataBuilder();
    data.setText(textData);

    clipboard.write(data.build());
});

export const copyStyle = defineCommand('copyStyle', 'Copy Style', (r) => {

    const clipboard = r.call(ClipboardService);
    const selection = r.call(EditorSession).get(Selection);

    if (!selection.size) return;

    // Just build up a massive list of style values
    const components = Physicals.top(selection).flatMap(x => x.flatten(true));
    const styles = {} as Record<string, any>;

    for (const c of components) {
        const ct = Component.describe(c);
        const cs = styles[ct.name] = styles[ct.name] || {};
        for (const cp of ct.properties.filter(x => !!x.style)) {
            cs[cp.name] = c.get(cp.name);
        }
    }

    const textData = JSON.stringify(DeqoStyleData.wrap(styles), null, 4);

    const data = new ClipDataBuilder();
    data.setText(textData);

    // We need to do this for fallback clipboard:
    data.setDeqoData(textData);

    clipboard.write(data.build());

    // Reset paste count to prevent paste stacking further
    clipboardStore.update({ pasteCount: 0 });

});

export const paste = defineCommand('paste', 'Paste', async (r) => {

    const clipboard = r.call(ClipboardService);
    const injector = r.call(ContentInjector);

    const data = await clipboard.read();
    await injector.injectFromClipData(data);
});

export const pasteStyle = defineCommand('pasteStyle', 'Paste Style', async (r) => {

    const clipboard = r.call(ClipboardService);
    const injector = r.call(ContentInjector);

    const data = await clipboard.read();
    if (!data.has('text/plain')) return;
    const text = await data.get('text/plain').text();
    if (!DeqoStyleData.is(text)) return;

    await injector.injectFromClipData(data);
});

export const inClipboardCommands = () => inCommands(copy, copyData, copyJson, copyStyle, paste, pasteStyle);
